import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CHANNEL_LABEL, formatDate } from "@/lib/format";
import { Mail, Smartphone, ShieldAlert, DollarSign, ScrollText, Building2, Users as UsersIcon, Upload, Trash2, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatRut, isValidRut, cleanRut } from "@/lib/rut";
import { useConfirm } from "@/components/ConfirmProvider";

export default function Configuracion() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Configuración</h1>
        <p className="text-slate-500 mt-1">Empresa, usuarios, precios, anti-spam y auditoría.</p>
      </div>

      <Tabs defaultValue="empresa">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="empresa" data-testid="tab-empresa">Empresa</TabsTrigger>
          <TabsTrigger value="usuarios" data-testid="tab-usuarios">Usuarios</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing">Precios</TabsTrigger>
          <TabsTrigger value="antispam" data-testid="tab-antispam">Anti-spam</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Auditoría</TabsTrigger>
        </TabsList>

        <TabsContent value="empresa" className="mt-4">
          <EmpresaPanel canEdit={isAdmin} />
        </TabsContent>

        <TabsContent value="usuarios" className="mt-4">
          {isAdmin ? <UsuariosPanel currentUser={user} /> : <RestrictedCard />}
        </TabsContent>

        <TabsContent value="pricing" className="mt-4">
          <PricingPanel />
        </TabsContent>

        <TabsContent value="antispam" className="mt-4">
          <AntiSpamPanel canEdit={isAdmin} />
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <AuditPanel enabled={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------- Empresa -------------------- */
function EmpresaPanel({ canEdit }) {
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ name: "", razon_social: "", rut: "" });
  const [saving, setSaving] = useState(false);

  const reload = () =>
    api.get("/organization").then((r) => {
      setOrg(r.data);
      setForm({ name: r.data.name || "", razon_social: r.data.razon_social || "", rut: r.data.rut || "" });
    });

  // Al subir/borrar logo NO toca el borrador del form (bug: perdía datos no guardados)
  const refreshLogoFlag = () =>
    api.get("/organization").then((r) => setOrg((prev) => ({ ...(prev || {}), has_logo: r.data.has_logo })));

  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (form.rut && !isValidRut(form.rut)) { toast.error("RUT inválido"); return; }
    setSaving(true);
    try {
      await api.put("/organization", { name: form.name.trim(), razon_social: form.razon_social.trim(), rut: form.rut.trim() });
      toast.success("Datos de la empresa guardados");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (!org) return <div className="p-6 text-sm text-slate-400">Cargando…</div>;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6 max-w-3xl">
      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-slate-600" />
        <h2 className="font-display font-semibold text-slate-800">Datos de la empresa contratista</h2>
      </div>

      <OrgLogoUploader onChanged={refreshLogoFlag} canEdit={canEdit} hasLogo={org.has_logo} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nombre comercial">
          <input data-testid="org-name" disabled={!canEdit} value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm disabled:bg-slate-50" />
        </Field>
        <Field label="Razón social">
          <input data-testid="org-razon-social" disabled={!canEdit} value={form.razon_social}
            onChange={(e) => setForm({ ...form, razon_social: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm disabled:bg-slate-50" />
        </Field>
        <Field label="RUT">
          <input data-testid="org-rut" disabled={!canEdit} value={form.rut}
            onChange={(e) => setForm({ ...form, rut: formatRut(e.target.value) })}
            onBlur={(e) => setForm({ ...form, rut: cleanRut(e.target.value) ? formatRut(e.target.value) : "" })}
            placeholder="76.123.456-7"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm font-mono disabled:bg-slate-50" />
        </Field>
      </div>

      {canEdit && (
        <div>
          <button data-testid="save-org-button" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg">
            {saving && <Loader2 size={14} className="animate-spin" />} Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
}

function OrgLogoUploader({ hasLogo, canEdit, onChanged }) {
  const confirmAction = useConfirm();
  const [objectUrl, setObjectUrl] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!hasLogo) { setObjectUrl(null); return; }
    let cancelled = false;
    api.get("/organization/logo", { responseType: "blob" })
      .then((r) => { if (!cancelled) setObjectUrl(URL.createObjectURL(r.data)); })
      .catch(() => { if (!cancelled) setObjectUrl(null); });
    return () => { cancelled = true; };
  }, [hasLogo, reloadKey]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Máximo 1MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/organization/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado");
      setReloadKey((k) => k + 1);
      onChanged?.();
      window.dispatchEvent(new Event("organization-logo-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo subir"); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    const ok = await confirmAction({ title: "Eliminar logo", description: "¿Quitar el logo de la organización?", confirmText: "Eliminar", variant: "danger" });
    if (!ok) return;
    try {
      await api.delete("/organization/logo");
      setObjectUrl(null);
      toast.success("Logo eliminado");
      onChanged?.();
      window.dispatchEvent(new Event("organization-logo-updated"));
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="flex items-center gap-5">
      <div className="h-24 w-24 rounded-xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
        {objectUrl
          ? <img data-testid="org-logo-preview" src={objectUrl} alt="Logo empresa" className="h-full w-full object-contain" />
          : <Building2 size={28} className="text-slate-300" />}
      </div>
      {canEdit && (
        <div className="space-y-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          <div className="flex gap-2">
            <button data-testid="upload-org-logo" onClick={() => inputRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Subir logo
            </button>
            {hasLogo && (
              <button data-testid="delete-org-logo" onClick={remove}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium border border-red-200 bg-white text-red-600 hover:bg-red-50">
                <Trash2 size={14} /> Eliminar
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400">PNG, JPG, WEBP o SVG · máx. 1MB</p>
        </div>
      )}
    </div>
  );
}

/* -------------------- Usuarios -------------------- */
function UsuariosPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);

  const reload = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between p-5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <UsersIcon size={18} className="text-slate-600" />
          <h2 className="font-display font-semibold text-slate-800">Usuarios de la empresa</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">{users.length} usuario(s)</span>
          <button
            data-testid="new-user-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">
            + Nuevo usuario
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-slate-400">Cargando…</div>
      ) : (
        <table data-testid="users-table" className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-slate-500 border-b border-slate-100 bg-slate-50">
              <th className="text-left py-2.5 px-4">Nombre</th>
              <th className="text-left py-2.5 px-4">Correo</th>
              <th className="text-left py-2.5 px-4">Rol</th>
              <th className="text-left py-2.5 px-4">Estado</th>
              <th className="text-right py-2.5 px-4">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-50">
                <td className="py-3 px-4">
                  <p className="font-medium text-slate-800">{u.name || "—"}</p>
                  {u.is_self && <span className="text-[11px] text-blue-600">Tú</span>}
                </td>
                <td className="py-3 px-4 text-slate-600">{u.email}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center h-7 px-2.5 rounded-md text-xs font-medium ${u.role === "ADMIN" ? "bg-indigo-50 text-indigo-700" : "bg-slate-100 text-slate-700"}`}>
                    {u.role === "ADMIN" ? "Admin" : "Acreditador"}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-medium ${u.active ? "text-emerald-700" : "text-slate-400"}`}>
                    {u.active ? "Activo" : "Desactivado"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="inline-flex gap-2">
                    <button
                      data-testid={`edit-user-${u.id}`}
                      onClick={() => setEditing(u)}
                      className="h-8 px-3 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                      Editar
                    </button>
                    <button
                      data-testid={`reset-pwd-${u.id}`}
                      onClick={() => setResetting(u)}
                      className="h-8 px-3 rounded-md text-xs font-medium border border-amber-200 bg-white text-amber-700 hover:bg-amber-50">
                      Resetear contraseña
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-sm text-slate-400">Sin usuarios.</td></tr>
            )}
          </tbody>
        </table>
      )}
      <p className="text-xs text-slate-400 p-4 border-t border-slate-100">
        Un Admin puede crear, configurar y gestionar la empresa. Un Acreditador puede operar en los módulos de Acreditación y Trabajadores.
      </p>

      {editing && (
        <UserEditDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setUsers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
          }}
        />
      )}
      {creating && (
        <UserCreateDialog
          onClose={() => setCreating(false)}
          onCreated={(nu) => { setUsers((prev) => [...prev, nu]); setCreating(false); }}
        />
      )}
      {resetting && (
        <ResetPasswordDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}
    </div>
  );
}

function UserEditDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "", email: user.email || "",
    role: user.role, active: !!user.active,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/users/${user.id}`, form);
      if (data.unchanged) {
        toast.info("Sin cambios");
        onClose();
      } else {
        toast.success("Usuario actualizado");
        onSaved(data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
    finally { setSaving(false); }
  };
  const canDisableSelf = !user.is_self;
  const canDemoteSelf = !(user.is_self && user.role === "ADMIN");
  return (
    <ModalShell title={`Editar · ${user.name || user.email}`} onClose={onClose}>
      <div className="space-y-3">
        <FieldLabel>Nombre</FieldLabel>
        <input
          data-testid="edit-user-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <FieldLabel>Correo</FieldLabel>
        <input
          data-testid="edit-user-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <FieldLabel>Rol</FieldLabel>
        <Select
          value={form.role}
          onValueChange={(v) => setForm({ ...form, role: v })}
          disabled={!canDemoteSelf}>
          <SelectTrigger data-testid="edit-user-role" className="h-10 bg-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="ADMIN">Admin</SelectItem>
            <SelectItem value="ACREDITADOR">Acreditador</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center justify-between pt-2">
          <FieldLabel className="!mb-0">Estado</FieldLabel>
          <div className="flex items-center gap-2">
            <Switch
              data-testid="edit-user-active"
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })}
              disabled={!canDisableSelf} />
            <span className={`text-xs font-medium ${form.active ? "text-emerald-700" : "text-slate-400"}`}>
              {form.active ? "Activo" : "Desactivado"}
            </span>
          </div>
        </div>
        {!canDemoteSelf && (
          <p className="text-xs text-amber-600">No puedes quitarte tu propio rol de Admin.</p>
        )}
      </div>
      <ModalFooter>
        <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
        <button
          data-testid="edit-user-save"
          onClick={save} disabled={saving}
          className="h-10 px-4 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function UserCreateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", role: "ACREDITADOR", password: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success("Usuario creado");
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title="Nuevo usuario" onClose={onClose}>
      <div className="space-y-3">
        <FieldLabel>Nombre</FieldLabel>
        <input
          data-testid="new-user-name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <FieldLabel>Correo</FieldLabel>
        <input
          data-testid="new-user-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        <FieldLabel>Rol</FieldLabel>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger data-testid="new-user-role" className="h-10 bg-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="ADMIN">Admin</SelectItem>
            <SelectItem value="ACREDITADOR">Acreditador</SelectItem>
          </SelectContent>
        </Select>
        <FieldLabel>Contraseña inicial</FieldLabel>
        <input
          data-testid="new-user-password"
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder="Mínimo 6 caracteres"
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
      </div>
      <ModalFooter>
        <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
        <button
          data-testid="new-user-save"
          onClick={save} disabled={saving || !form.email || !form.name || form.password.length < 6}
          className="h-10 px-4 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {saving ? "Creando…" : "Crear usuario"}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { password });
      toast.success(`Contraseña actualizada para ${user.email}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell title={`Resetear contraseña · ${user.name || user.email}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Define una nueva contraseña para <span className="font-medium">{user.email}</span>. El usuario podrá iniciar sesión con ella de inmediato.
        </p>
        <FieldLabel>Nueva contraseña</FieldLabel>
        <input
          data-testid="reset-pwd-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Mínimo 6 caracteres"
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
      </div>
      <ModalFooter>
        <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
        <button
          data-testid="reset-pwd-save"
          onClick={save} disabled={saving || password.length < 6}
          className="h-10 px-4 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50">
          {saving ? "Guardando…" : "Resetear"}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function FieldLabel({ children, className = "" }) {
  return <label className={`block text-xs font-medium text-slate-500 mb-1 ${className}`}>{children}</label>;
}

function ModalFooter({ children }) {
  return <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-slate-100">{children}</div>;
}

/* -------------------- Pricing / Anti-spam / Audit -------------------- */
function PricingPanel() {
  const [pricing, setPricing] = useState([]);
  useEffect(() => { api.get("/settings/pricing").then((r) => setPricing(r.data)); }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <h2 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2"><DollarSign size={18} className="text-emerald-600" /> Reglas de precio</h2>
      <table className="w-full text-sm">
        <thead><tr className="text-xs uppercase text-slate-500 border-b border-slate-100"><th className="text-left py-2">Canal</th><th className="text-left py-2">Proveedor</th><th className="text-left py-2">País</th><th className="text-right py-2">Precio unit.</th><th className="text-right py-2">Markup</th><th className="text-left py-2 pl-3">Moneda</th></tr></thead>
        <tbody>
          {pricing.map((r) => (
            <tr key={r.id} className="border-b border-slate-50"><td className="py-2.5">{CHANNEL_LABEL[r.channel]}</td><td className="py-2.5">{r.provider}</td><td className="py-2.5">{r.country}</td><td className="py-2.5 text-right font-mono">{r.unit_price}</td><td className="py-2.5 text-right font-mono">{r.provider_markup}</td><td className="py-2.5 pl-3">{r.currency}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AntiSpamPanel({ canEdit }) {
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/settings/organization").then((r) => setOrg(r.data)); }, []);
  const save = async () => {
    await api.put("/settings/organization", { anti_spam: org.anti_spam });
    toast.success("Reglas anti-spam guardadas");
  };
  if (!org) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4 max-w-lg">
      <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><ShieldAlert size={18} className="text-amber-600" /> Protección anti-spam</h2>
      <Field label="Horas mínimas entre mensajes"><input type="number" disabled={!canEdit} value={org.anti_spam.minimum_hours_between_messages} onChange={(e) => setOrg({ ...org, anti_spam: { ...org.anti_spam, minimum_hours_between_messages: parseInt(e.target.value || 0) } })} className="w-24 px-3 py-1.5 rounded-lg border border-slate-200 text-sm" /></Field>
      <Field label="Máximo de recordatorios"><input type="number" disabled={!canEdit} value={org.anti_spam.max_reminders} onChange={(e) => setOrg({ ...org, anti_spam: { ...org.anti_spam, max_reminders: parseInt(e.target.value || 0) } })} className="w-24 px-3 py-1.5 rounded-lg border border-slate-200 text-sm" /></Field>
      <Field label="Detener al recibir respuesta"><Switch disabled={!canEdit} checked={org.anti_spam.stop_after_response} onCheckedChange={(v) => setOrg({ ...org, anti_spam: { ...org.anti_spam, stop_after_response: v } })} /></Field>
      <p className="text-xs text-slate-400">No se envía a quienes ya completaron 100%, marcaron no continuar, hicieron opt-out o alcanzaron el máximo de intentos.</p>
      {canEdit && <button data-testid="save-antispam" onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">Guardar</button>}
    </div>
  );
}

function AuditPanel({ enabled }) {
  const [audit, setAudit] = useState([]);
  useEffect(() => { if (enabled) api.get("/audit-logs").then((r) => setAudit(r.data)).catch(() => {}); }, [enabled]);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <h2 className="font-display font-semibold text-slate-800 mb-4 flex items-center gap-2"><ScrollText size={18} className="text-slate-600" /> Registro de auditoría</h2>
      <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
        {audit.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Sin registros.</p> : audit.map((a) => (
          <div key={a.id} className="flex items-center justify-between text-sm p-2.5 rounded-lg hover:bg-slate-50 border-b border-slate-50">
            <div><span className="font-medium text-slate-700">{a.action}</span> <span className="text-slate-400">· {a.entity}</span> <span className="text-slate-500">por {a.user_name || a.user_email}</span></div>
            <span className="text-xs text-slate-400">{formatDate(a.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RestrictedCard() {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center text-sm text-slate-400">
      Solo los usuarios con rol Admin pueden gestionar esta sección.
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      {children}
    </div>
  );
}
